import { Injectable, signal } from "@angular/core";
import { Observable, Subject } from "rxjs";

export interface GlobalAlertConfig {
  id?: string;
  title?: string;
  message: string;
  htmlMessage?: string; // Optional HTML content for rich formatting. Note: Angular sanitizes by default when using [innerHTML]
  type?: "info" | "warning" | "error" | "success";
  showCancel?: boolean;
  confirmText?: string;
  cancelText?: string;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

export interface GlobalAlertResult {
  confirmed: boolean;
  cancelled: boolean;
}

@Injectable({
  providedIn: "root",
})
export class GlobalAlertService {
  private _isVisible = signal<boolean>(false);
  private _currentAlert = signal<GlobalAlertConfig | null>(null);

  readonly isVisible = this._isVisible.asReadonly();
  readonly currentAlert = this._currentAlert.asReadonly();

  private currentResultSubject: Subject<GlobalAlertResult> | null = null;

  /**
   * Show a global alert with confirmation capabilities
   * @param config Alert configuration
   * @returns Observable that emits the user's choice
   */
  show(config: GlobalAlertConfig): Observable<GlobalAlertResult> {
    // Create a new Subject for this specific alert to prevent cross-contamination
    this.currentResultSubject = new Subject<GlobalAlertResult>();

    // Set default values
    const alertConfig: GlobalAlertConfig = {
      id: Date.now().toString(),
      type: "info",
      showCancel: true,
      confirmText: "OK",
      cancelText: "Cancel",
      autoClose: false,
      autoCloseDelay: 5000,
      ...config,
    };

    this._currentAlert.set(alertConfig);
    this._isVisible.set(true);

    // Auto-close logic if enabled
    if (alertConfig.autoClose && alertConfig.autoCloseDelay) {
      setTimeout(() => {
        this.dismiss(false);
      }, alertConfig.autoCloseDelay);
    }

    return this.currentResultSubject.asObservable();
  }

  /**
   * Confirm the alert
   */
  confirm(): void {
    this.dismiss(true);
  }

  /**
   * Cancel the alert
   */
  cancel(): void {
    this.dismiss(false);
  }

  /**
   * Dismiss the alert with result
   */
  private dismiss(confirmed: boolean): void {
    this._isVisible.set(false);

    const result: GlobalAlertResult = {
      confirmed,
      cancelled: !confirmed,
    };

    // Emit result to the current alert's subscribers only
    if (this.currentResultSubject) {
      this.currentResultSubject.next(result);
      this.currentResultSubject.complete();
      this.currentResultSubject = null;
    }

    // Clear alert after animation
    setTimeout(() => {
      this._currentAlert.set(null);
    }, 300);
  }

  /**
   * Quick methods for common alert types
   */
  info(message: string, title?: string): Observable<GlobalAlertResult> {
    return this.show({
      type: "info",
      message,
      title: title || "Information",
      showCancel: false,
      confirmText: "OK",
    });
  }

  warning(message: string, title?: string): Observable<GlobalAlertResult> {
    return this.show({
      type: "warning",
      message,
      title: title || "Warning",
      showCancel: true,
    });
  }

  error(message: string, title?: string): Observable<GlobalAlertResult> {
    return this.show({
      type: "error",
      message,
      title: title || "Error",
      showCancel: false,
      confirmText: "OK",
    });
  }

  success(message: string, title?: string): Observable<GlobalAlertResult> {
    return this.show({
      type: "success",
      message,
      title: title || "Success",
      showCancel: false,
      confirmText: "OK",
      autoClose: true,
    });
  }

  /**
   * Confirmation dialog replacement for window.confirm()
   */
  confirmDialog(
    message: string,
    title?: string
  ): Observable<GlobalAlertResult> {
    return this.show({
      type: "warning",
      message,
      title: title || "Confirm Action",
      showCancel: true,
      confirmText: "Yes, Continue",
      cancelText: "Cancel",
    });
  }

  /**
   * HTML Message Methods - for rich formatting
   * Note: Angular automatically sanitizes HTML content when using [innerHTML] to prevent XSS attacks.
   * Script tags and other dangerous elements are automatically removed.
   */

  infoHtml(
    htmlMessage: string,
    message?: string,
    title?: string
  ): Observable<GlobalAlertResult> {
    return this.show({
      type: "info",
      message: message || "Information",
      htmlMessage,
      title: title || "Information",
      showCancel: false,
      confirmText: "OK",
    });
  }

  warningHtml(
    htmlMessage: string,
    message?: string,
    title?: string
  ): Observable<GlobalAlertResult> {
    return this.show({
      type: "warning",
      message: message || "Warning",
      htmlMessage,
      title: title || "Warning",
      showCancel: true,
    });
  }

  errorHtml(
    htmlMessage: string,
    message?: string,
    title?: string
  ): Observable<GlobalAlertResult> {
    return this.show({
      type: "error",
      message: message || "Error",
      htmlMessage,
      title: title || "Error",
      showCancel: false,
      confirmText: "OK",
    });
  }

  successHtml(
    htmlMessage: string,
    message?: string,
    title?: string
  ): Observable<GlobalAlertResult> {
    return this.show({
      type: "success",
      message: message || "Success",
      htmlMessage,
      title: title || "Success",
      showCancel: false,
      confirmText: "OK",
      autoClose: true,
    });
  }
}
